/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { isArray, isObject } from "../types/is.mjs";
import { typeOf } from "../types/typeof.mjs";

export { extend };

/**
 * Extend copies all enumerable own properties from one or
 * more source objects to a target object. It returns the modified target object.
 *
 * @return {object}
 * @license AGPLv3
 * @since 1.10.0
 * @copyright schukai GmbH
 * @memberOf Monster.Data
 * @throws {Error} unsupported argument
 * @throws {Error} type mismatch
 * @throws {Error} unsupported argument
 * @param args
 */
function extend(...args) {
	let o;
	let i;

	if (typeof args !== "object" || args[0] === null) {
		throw new Error(`unsupported argument ${JSON.stringify(args[0])}`);
	}

	for (i = 0; i < args.length; i++) {
		const a = args[i];

		if (!(isObject(a) || isArray(a))) {
			throw new Error(`unsupported argument ${JSON.stringify(a)}`);
		}

		if (o === undefined) {
			o = a;
			continue;
		}

		for (const k in a) {
			const v = a?.[k];

			if (v === o?.[k]) {
				continue;
			}

			if ((isObject(v) && typeOf(v) === "object") || isArray(v)) {
				if (o[k] === undefined) {
					if (isArray(v)) {
						o[k] = [];
					} else {
						o[k] = {};
					}
				} else {
					if (typeOf(o[k]) !== typeOf(v)) {
						throw new Error(
							`type mismatch: ${JSON.stringify(o[k])}(${typeOf(
								o[k],
							)}) != ${JSON.stringify(v)}(${typeOf(v)})`,
						);
					}
				}

				if (isArray(o[k])) {
					o[k] = [];
					o[k].push(...v);
					continue;
				}

				o[k] = extend(o[k], v);
			} else {
				if (isArray(o)) {
					o.push(v);
					continue;
				}
				o[k] = v;
			}
		}
	}

	return o;
}
